import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@timeedit/ui-components';

export const SortableTableRow = (
  props: HTMLAttributes<HTMLTableRowElement> & {
    'data-row-key': string | number;
  }
) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(props['data-row-key']) });

  const { children, ...restProps } = props;

  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...restProps}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

export const SortableDragHandle = () => (
  <span style={{ display: 'inline-flex' }}>
    <Icon id="HolderOutlined" alt="Drag" />
  </span>
);

type SortableTableWrapperProps<T> = {
  items: T[];
  getId: (item: T, index: number) => string | number;
  onReorder: (newItems: T[]) => void;
  children: ReactNode;
};

export function SortableTableWrapper<T>({
  items,
  getId,
  onReorder,
  children,
}: SortableTableWrapperProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } })
  );

  const ids = items.map((item, i) => String(getId(item, i)));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = ids.findIndex((id) => id === active.id);
    const newIndex = ids.findIndex((id) => id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}
